import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, ErrorMessage, Toast } from '../ui';
import useNote from '../../hooks/useNote';
import useNoteAccess from '../../hooks/useNoteAccess';
import { getRateLimitState } from '../../services/rateLimit';
import { supabase } from '../../lib/supabase';
import Header from './Header';
import AccessForm from './AccessForm';
import Content from './Content';
import BlockedPage from '../BlockedPage';

export default function ViewNote() {
  const { t } = useTranslation('viewNote');
  const { shortId } = useParams<{ shortId: string }>();
  const { note, loading, error } = useNote(shortId);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<string | null>(null);
  
  const {
    password,
    setPassword,
    content,
    showContent,
    setShowContent,
    toast,
    setToast,
    handleAccess,
    copyContent,
    isDecrypting
  } = useNoteAccess({ 
    note, 
    id: shortId,
    onError: async (error) => {
      if (error.message.includes('6 characters')) {
        setValidationError(t('form.password.validation.tooShort', { min: 6 }));
      } else if (error.message === 'Invalid password') {
        try {
          if (!shortId) {
            console.error('Note ID is missing, cannot check rate limit');
            setValidationError(t('errors.notFound.message'));
            return;
          }

          console.log('Invalid password attempt, checking rate limit state');
          const rateLimitState = await getRateLimitState(shortId);
          console.log('Rate limit state after failed attempt:', rateLimitState);
          
          if (rateLimitState.isBlocked && rateLimitState.blockedUntil) {
            console.log('User is blocked, updating state');
            setIsBlocked(true);
            setBlockedUntil(rateLimitState.blockedUntil);
          } else {
            console.log('User is not blocked, showing error');
            setValidationError(t('form.password.validation.invalid'));
          }
        } catch (rateError) {
          console.error('Failed to check rate limit state:', rateError);
          setValidationError(t('form.password.validation.invalid'));
        }
      } else {
        setValidationError(error.message);
      }
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!shortId) {
      console.error('Note ID is missing, cannot check rate limit');
      setValidationError(t('errors.notFound.message'));
      return;
    }
    
    try {
      const rateLimitState = await getRateLimitState(shortId);
      
      if (rateLimitState.isBlocked && rateLimitState.blockedUntil) {
        setIsBlocked(true);
        setBlockedUntil(rateLimitState.blockedUntil);
        return;
      }
      
      await handleAccess(e);
    } catch (error) {
      console.error('Failed to check rate limit state:', error);
      await handleAccess(e);
    }
  };

  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    setValidationError(null);
  };

  if (isBlocked && blockedUntil) {
    console.log('Rendering BlockedPage');
    return <BlockedPage blockedUntil={blockedUntil} />;
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-sm border p-8 flex items-center justify-center">
          <LoadingSpinner size="lg" className="mr-3" />
          <span className="text-gray-600">{t('loading.title')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <ErrorMessage message={error.message} />
      </div>
    );
  }

  if (!note) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <ErrorMessage 
          title={t('errors.notFound.title')}
          message={t('errors.notFound.message')} 
        />
      </div>
    );
  }

  if (new Date(note.expiresat) < new Date()) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <ErrorMessage 
          title={t('errors.expired.title')}
          message={t('errors.expired.message')} 
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Header note={note} />
      
      {!content ? (
        <AccessForm
          note={note}
          password={password}
          isDecrypting={isDecrypting}
          error={validationError}
          onPasswordChange={handlePasswordChange}
          onSubmit={handleSubmit}
          isBlocked={isBlocked}
        />
      ) : (
        <Content
          content={content}
          showContent={showContent}
          onToggleVisibility={() => setShowContent(!showContent)}
          onCopy={copyContent}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}